import { Alert, List, Stack, Text } from '@mantine/core';
import dayjs from 'dayjs';
import type { FC } from 'react';

import { useI18n } from '../../hooks/useI18n.ts';

interface DeclineEntry {
  declined_at: string;
  reason: string | null;
}

const isDeclineEntry = (value: unknown): value is DeclineEntry =>
  typeof value === 'object' && value !== null && typeof (value as { declined_at?: unknown }).declined_at === 'string';

const formatDate = (date: string) => dayjs(date).format('D MMM YYYY, HH:mm');

interface DeclineNoticeProps {
  declineReason?: string | null;
  /** `events.decline_history`, oldest first. The last entry is the current decline. */
  declineHistory?: unknown;
}

/** Why the event was declined, with the earlier declines below it. */
export const DeclineNotice: FC<DeclineNoticeProps> = ({ declineReason, declineHistory }) => {
  const { t } = useI18n();

  const history = Array.isArray(declineHistory) ? declineHistory.filter(isDeclineEntry) : [];
  const current = history.at(-1);
  const earlier = history.slice(0, -1).toReversed();
  const noReason = t('event-edit_declined_no-reason') ?? 'No reason given.';

  return (
    <Alert color="red" title={t('event-edit_declined_title') ?? 'This event was declined'} variant="light">
      <Stack gap="sm">
        <Text size="sm">
          {t('event-edit_declined_description') ??
            'Make the changes asked for below, then resubmit the event for review.'}
        </Text>

        <Stack gap={2}>
          {current ? (
            <Text c="dimmed" size="xs">
              {formatDate(current.declined_at)}
            </Text>
          ) : null}
          <Text fw={600} size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {t('event-panel_label--decline-reason') ?? 'Reason'}: {declineReason || noReason}
          </Text>
        </Stack>

        {earlier.length > 0 ? (
          <Stack gap={4}>
            <Text fw={600} size="sm">
              {t('event-edit_declined_history-title') ?? 'Earlier declines'}
            </Text>
            <List size="sm" spacing={4}>
              {earlier.map((entry) => (
                <List.Item key={entry.declined_at}>
                  <Text c="dimmed" component="span" size="xs">
                    {formatDate(entry.declined_at)}
                  </Text>{' '}
                  <Text component="span" size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {entry.reason ?? noReason}
                  </Text>
                </List.Item>
              ))}
            </List>
          </Stack>
        ) : null}
      </Stack>
    </Alert>
  );
};
